/* eslint-disable import/prefer-default-export */
/**
 * A canned `ReaderAiService` for the fixture routes.
 *
 * Deliberately outside `shell/`, like `local-data-service`: the extracted
 * package carries the contract, never an implementation. This one answers from
 * a table keyed on words in the prompt, so the demo shows real retrieval shape
 * — an answer, several passages, page numbers that navigate — without a course,
 * a token, or an indexed material.
 *
 * It is honest about being canned: an unmatched question returns no references
 * and says so, which is the same empty state the real service produces when
 * nothing clears the score threshold. That path is worth being able to see.
 *
 * The delay is not decoration. Retrieval takes seconds in production, the panel
 * has a loading state and a disabled composer because of it, and a fixture that
 * resolves instantly would let both rot untested.
 */
import type {
  ReaderAiAnswer,
  ReaderAiReference,
  ReaderAiService,
  ReaderAiSummary,
} from './shell/ai-service';

const THINKING_MS = 600;

const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => {
    setTimeout(() => resolve(value), THINKING_MS);
  });

/** Passages from the fixture PDF, with the pages they really sit on. */
const PASSAGES: Array<ReaderAiReference & { keywords: string[] }> = [
  {
    id: 'chunk-1',
    pageNumber: 1,
    keywords: ['release', 'update', 'summary', 'overview', 'period'],
    content:
      'Product Release Update, March – July 2026: 13 releases delivered across 16 weeks.',
  },
  {
    id: 'chunk-2',
    pageNumber: 2,
    keywords: ['glance', 'progress', 'delivery', 'platform'],
    content:
      'The Period at a Glance — consistent delivery and measurable progress across the whole platform.',
  },
  {
    id: 'chunk-5',
    pageNumber: 5,
    keywords: ['wizard', 'steps', 'flow', 'simplify', 'course'],
    content:
      'Redundant wizard steps were removed from course creation, shortening the path to a published course.',
  },
];

const SUMMARY: ReaderAiSummary = {
  short:
    'A review of thirteen platform releases shipped between March and July 2026, grouped by the area each one improved.',
  long: 'A review of thirteen platform releases shipped between March and July 2026, grouped by the area each one improved. The through-line is removing steps rather than adding features: redundant wizard stages were cut from course creation, material handling gained in-place replacement, and the reader was rebuilt around a single shell that serves every document type. Where new surfaces were added they were added behind course-level flags, so an institution adopts them on its own schedule.',
};

export const fixtureAiService: ReaderAiService = {
  async ask(_documentId: string, prompt: string): Promise<ReaderAiAnswer> {
    const words: string[] = prompt.toLowerCase().match(/[a-z]{3,}/g) ?? [];
    const references = PASSAGES.filter((passage) =>
      passage.keywords.some((keyword) => words.includes(keyword))
    ).map(({ keywords, ...reference }) => reference);

    return delay({
      query: prompt,
      // Deliberately not a copy of the first passage: the real service writes
      // over the retrieved set, and a fixture that echoed one reference would
      // make the panel look like it was rendering the same text twice.
      answer: references.length
        ? `This document covers that across ${references.length} passage${
            references.length === 1 ? '' : 's'
          }, listed below — the clearest is on page ${references[0].pageNumber}.`
        : undefined,
      references,
    });
  },

  async getSummary(): Promise<ReaderAiSummary> {
    return delay(SUMMARY);
  },
};
